import React, { useState, useEffect } from "react";
import { Card } from "react-bootstrap";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import FormikContainer from "../../../components/form/FormikContainer.jsx";
import DeleteConfirmationModal from "../../../components/modal/DeleteConfirmationModal.jsx";
import apiMessages from "../../../lib/apiMessages.js";
import genericTranslations from "../../../utils/genericTranslations.js";
import { selectSoftwarePackageById } from "../selectors";
import {
  fetchSoftwarePackageById,
  removeSoftwarePackage,
  saveSoftwarePackage,
} from "../thunks.js";

import SoftwarePackageForm from "./SoftwarePackageForm";

const messages = {
  softwarePackage: {
    id: "modules.SoftwarePackage.components.SoftwarePackageEditor.softwarePackage",
    description: "Application list",
    defaultMessage: "Application",
  },
  notFoundMessage: {
    id: "modules.SoftwarePackage.components.SoftwarePackageEditor.notFoundMessage",
    description: "Error message fetching the software packages",
    defaultMessage: "Couldn't find the package!",
  },
  deleteSoftwarePackage: {
    id: "modules.SoftwarePackage.components.SoftwarePackageEditor.deleteSoftwarePackage",
    description: "Delete the software packages",
    defaultMessage: "Delete package",
  },
  deleteMessageConfirmation: {
    id: "modules.SoftwarePackage.components.SoftwarePackageEditor.deleteMessageConfirmation",
    description: "Confirmation message delete software package",
    defaultMessage: "Are you sure you want to delete this package?",
  },
  serverValidationAlert: {
    id: "modules.SoftwarePackage.components.SoftwarePackageEditor.serverValidationAlert",
    description: "Software Package validation error message.",
    defaultMessage:
      "A record already exists with id {id} or tag {tag}. Please use unique values!",
  },
};

const SoftwarePackageEditor = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { identifier } = useParams();

  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [serverValidationMessage, setServerValidationMessage] = useState("");

  const softwarePackage = useSelector((state) =>
    selectSoftwarePackageById(state, { identifier }),
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchSoftwarePackageById(identifier));
      } catch (err) {
        setError(err);
      }
    };

    if (identifier && !softwarePackage) {
      fetchData();
    }
  }, [softwarePackage, identifier, dispatch]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);

    try {
      await dispatch(
        saveSoftwarePackage(
          values,
          softwarePackage ? softwarePackage.id : undefined,
        ),
      );
      navigate("/software-package", { replace: true });
    } catch (err) {
      if (err.message === apiMessages.ERROR_RECORD_ALREADY_EXIST) {
        setServerValidationMessage(
          intl.formatMessage(messages.serverValidationAlert, {
            id: values.code,
            tag: values.tag,
          }),
        );
      } else {
        setError(`Er ging iets fout bij het opslaan: ${err}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const validate = (values) => {
    const errors = {};
    const required = ["code", "title", "tag"];
    required.forEach((field) => {
      if (!values[field]) errors[field] = "Verplicht";
    });
    return errors;
  };

  const handleRemoveSoftwarePackage = async () => {
    try {
      await dispatch(removeSoftwarePackage(softwarePackage.id));
      navigate("/software-package", { replace: true });
    } catch (err) {
      setError(`Er ging iets fout bij het opslaan: ${err}`);
    }
  };

  if (error) {
    return (
      <strong>{intl.formatMessage(genericTranslations.errorMessage)} </strong>
    );
  }

  if (!softwarePackage && identifier) {
    return <strong>{intl.formatMessage(messages.notFoundMessage)}</strong>;
  }

  return (
    <Card>
      <Card.Header className="card-header-borderless" as="h3">
        {intl.formatMessage(messages.softwarePackage)}{" "}
        {softwarePackage
          ? intl.formatMessage(genericTranslations.edit).toLowerCase()
          : intl.formatMessage(genericTranslations.add).toLowerCase()}
      </Card.Header>
      <Card.Body>
        <FormikContainer
          initialValues={softwarePackage || {}}
          onSubmit={handleSubmit}
          validate={validate}
          formComponent={SoftwarePackageForm}
          extraProps={{
            onClickDelete: () => {
              setShowDeleteModal(true);
            },
            serverValidationMessage,
          }}
        />
        <DeleteConfirmationModal
          show={showDeleteModal}
          title={intl.formatMessage(messages.deleteSoftwarePackage)}
          bodyText={intl.formatMessage(messages.deleteMessageConfirmation)}
          confirmButtonText={intl.formatMessage(genericTranslations.delete)}
          cancelButtonText={intl.formatMessage(genericTranslations.close)}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleRemoveSoftwarePackage}
        />
      </Card.Body>
    </Card>
  );
};

export default SoftwarePackageEditor;
